import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Zap } from "lucide-react";
import { MaskedLine, Overline } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";

const HERO_IMG = "https://assets.spotlighthyperlocal.com/assets/3dc7de01-597d-4a2c-9e23-6a70769bacad/raw_creative/0ed85494-792c-45ef-945c-1d2ffc08a112.jpg";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "55%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-[#05141F]">
      <div className="absolute inset-0 hero-spotlight pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#FFFFFF]/10 blur-[140px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-32 pb-24 grid lg:grid-cols-12 gap-14 items-center">
        <motion.div style={{ y: textY, opacity: fade }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <Overline data-testid="hero-overline">Authorized Kia Dealership · Jajpur, Odisha — www.sanjibkia.com</Overline>
          </motion.div>

          <h1 data-testid="hero-heading" className="mt-6 font-display font-extrabold tracking-tight leading-[0.98] text-5xl sm:text-7xl lg:text-[5.6rem] text-white">
            <MaskedLine delay={0.35}>MOVEMENT</MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="text-outline-red">THAT</span> INSPIRES
            </MaskedLine>
            <MaskedLine delay={0.65}>
              <span className="text-[#FFFFFF]">EVERY DRIVE.</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
          >
            From the wild Sonet to the 708 km EV6 — explore the complete Kia India lineup at
            Sanjib Kia, NH-20 Panikoili, with doorstep test drives, hassle-free exchange and instant finance.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-book-test-drive-button"
              onClick={() => scrollToSection("#test-drive")}
              className="group inline-flex items-center gap-3 bg-[#FFFFFF] hover:bg-[#D4D4D4] text-black font-semibold uppercase tracking-[0.18em] text-sm px-8 py-4 clip-notch transition-colors duration-300"
            >
              Book a Test Drive
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-explore-lineup-button"
              onClick={() => scrollToSection("#lineup")}
              className="inline-flex items-center gap-3 border border-white/20 hover:border-[#FFFFFF] text-white font-semibold uppercase tracking-[0.18em] text-sm px-8 py-4 clip-notch transition-colors duration-300"
            >
              Explore Lineup
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4"
          >
            {[
              ["11", "Models on Display"],
              ["800V", "Ultra-Fast Charging"],
              ["100%", "Hassle-Free Exchange"],
            ].map(([v, l]) => (
              <div key={l} data-testid={`hero-stat-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p className="font-display font-extrabold text-2xl sm:text-3xl text-white">{v}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">{l}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 1.06, clipPath: "inset(100% 0 0 0)" }}
            animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative clip-hero overflow-hidden"
          >
            <motion.img
              src={HERO_IMG}
              alt="Kia Sorento — Global Icon. Reimagined for India."
              data-testid="hero-image"
              style={{ y: imgY, scale: 1.18 }}
              className="w-full h-[420px] sm:h-[540px] object-cover will-change-transform"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#05141F] via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFFFFF]">Featured</p>
                <p className="font-display font-bold text-white text-lg mt-1">Kia Sorento</p>
              </div>
              <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white bg-white/10 backdrop-blur-md border border-white/15 px-3 py-1.5">
                <Zap size={11} className="text-[#FFFFFF]" /> Hybrid
              </span>
            </div>
          </motion.div>
          <div className="absolute -inset-4 border border-[#FFFFFF]/25 clip-hero pointer-events-none translate-x-4 translate-y-4" />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-500"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="w-px h-8 bg-gradient-to-b from-[#FFFFFF] to-transparent" />
      </motion.div>
    </section>
  );
}
